import { ContextualMenuItemType, IContextualMenuItem, css } from 'office-ui-fabric-react';
import * as React from 'react';
import { connect } from 'react-redux';
import { RouteComponentProps, withRouter } from 'react-router-dom';
import { History } from 'history';
import { ApplicationState } from '../../store';
import { TenantState, Subscription, PPMFeatures } from '../../store/Tenant';
import { EntityType } from '../../entities/common';
import { RowMenu, RowMenuProps } from '../common/extensibleEntity/RowMenu';

export type MenuWithAuditLogProps = RowMenuProps & {
    entityType: EntityType;
};

type StateProps = { 
    tenant: TenantState;
};

type Props = MenuWithAuditLogProps & StateProps & RouteComponentProps<{}>;

/**
 * Builds an activity log menu item with upgrade icon if feature is not available
 * @param tenant - The tenant state containing subscription information
 * @param history - React router history for navigation
 * @param entityType - The type of entity (e.g., 'Project', 'Challenge', etc.)
 * @param entityId - Optional: The ID of the entity. If not provided, navigates to entity type list view
 * @returns A menu item for activity log, showing upgrade icon if feature is not enabled
 */
export const buildActivityLogMenuItem = (
    subscription: Subscription, 
    history: History, 
    entityType: string, 
    entityId?: string,
    onClick?: (text: string) => void
): IContextualMenuItem => {
    const hasFeature = Subscription.contains(subscription, PPMFeatures.AuditLog);
    const text = 'Activity Log';
    
    // Build the navigation URL based on whether entityId is provided
    const activityLogUrl = entityId
        ? `/activityLog/${entityType}/${entityId}`
        : `/activityLog/${entityType}`;
    
    return {
        key: 'activity-log',
        iconProps: { iconName: 'History' },
        className: css("activity-log-menu-item", !hasFeature && 'with-upgrade-icon'),
        name: text,
        text: text,
        title: hasFeature ? text : 'Upgrade Plan',
        onClick: () => {
            history.push(hasFeature ? activityLogUrl : "/billing");
            onClick?.(text);
        },
    };
};

export const AUDIT_LOG_SUPPORTED_ENTITIES = [
    EntityType.Portfolio,
    EntityType.Program,
    EntityType.Project, 
    EntityType.Challenge, 
    EntityType.Resource,
    EntityType.Roadmap,
    EntityType.Objective,
    EntityType.Idea,
];

const MenuWithAuditLog = (props: Props) => {
    const { tenant, entityType, history, item } = props;
    
    // Build audit log menu items if supported
    const auditLogCommands: IContextualMenuItem[] = [];
    if (AUDIT_LOG_SUPPORTED_ENTITIES.includes(entityType) && item?.id) {
        auditLogCommands.push(
            { key: 'audit-divider', itemType: ContextualMenuItemType.Divider },
            buildActivityLogMenuItem(tenant.subscription, history, entityType, item.id)
        );
    }
    
    const commands = [
        ...props.commands ?? [],
        ...auditLogCommands
    ];
    
    return <RowMenu
        {...props}
        commands={commands}
    />;
};

function mapStateToProps(state: ApplicationState): StateProps {
    return { 
        tenant: state.tenant
    }
}

export default withRouter<MenuWithAuditLogProps>(connect(mapStateToProps)(MenuWithAuditLog));
